/**
 * KB Domains — admin registry for the shared knowledge base.
 *
 * Extends the single shared KB (`kb_documents`) with a lightweight
 * classification. No parallel KB. Documents keep their content
 * untouched — assignment is a per-doc human decision handled in the
 * Unclassified queue below the registry table.
 *
 * Endpoints:
 *   GET    /api/admin/kb/domains
 *   POST   /api/admin/kb/domains
 *   PATCH  /api/admin/kb/domains/{slug}
 *   DELETE /api/admin/kb/domains/{slug}
 *   GET    /api/admin/kb/documents/unclassified?limit=200
 *   POST   /api/admin/kb/documents/{id}/domain   { domain: slug | null }
 */
import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, Save, Tag, FolderOpen, Shield, Lock,
} from "lucide-react";


const BLANK_DRAFT = {
  slug: "", name: "", description: "",
  enabled: true, member_visible: true,
};


export default function AdminKbDomains() {
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [domains, setDomains]       = useState([]);
  const [counts, setCounts]         = useState({});
  const [unclassified, setUnclassified] = useState([]);
  const [draft, setDraft]           = useState(BLANK_DRAFT);
  const [showCreate, setShowCreate] = useState(false);
  const [rowSaving, setRowSaving]   = useState({});
  // Bulk triage state — checkbox multi-select on the unclassified
  // queue, plus a source-ref prefix filter so admins can e.g. narrow
  // to `module:*` and assign the batch to Platform in one action.
  const [selectedIds, setSelectedIds] = useState({});   // { doc_id: true }
  const [queueFilter, setQueueFilter] = useState("");
  const [bulkTarget, setBulkTarget]   = useState("");
  const [bulkSaving, setBulkSaving]   = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [r, u] = await Promise.all([
        api.get("/admin/kb/domains"),
        api.get("/admin/kb/documents/unclassified", { params: { limit: 200 } }),
      ]);
      setDomains(r.data?.domains || []);
      setCounts(r.data?.counts || {});
      setUnclassified(u.data?.documents || []);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const unclassifiedCount = counts.unclassified || 0;

  // ─── create ──────────────────────────────────────────────────────────
  const validSlug = /^[a-z][a-z0-9_]{1,39}$/.test((draft.slug || "").trim());
  const canCreate = validSlug && (draft.name || "").trim().length > 0 && !saving;

  const submitCreate = async (e) => {
    e?.preventDefault?.();
    if (!canCreate) return;
    setSaving(true);
    try {
      await api.post("/admin/kb/domains", {
        slug: draft.slug.trim().toLowerCase(),
        name: draft.name.trim(),
        description: (draft.description || "").trim(),
        enabled: draft.enabled,
        member_visible: draft.member_visible,
      });
      toast.success(`Domain "${draft.name.trim()}" created`);
      setDraft(BLANK_DRAFT);
      setShowCreate(false);
      await refresh();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  // ─── patch a row ────────────────────────────────────────────────────
  const patchDomain = async (slug, patch) => {
    setRowSaving((m) => ({ ...m, [slug]: true }));
    try {
      const r = await api.patch(`/admin/kb/domains/${slug}`, patch);
      setDomains((prev) => prev.map((d) => (d.slug === slug ? r.data : d)));
    } catch (err) {
      toast.error(formatError(err));
      await refresh();
    } finally {
      setRowSaving((m) => ({ ...m, [slug]: false }));
    }
  };

  const deleteDomain = async (d) => {
    if (d.is_system) return;
    const n = counts[d.slug] || 0;
    if (n > 0) {
      toast.error(`Reclassify ${n} document(s) in "${d.name}" first`);
      return;
    }
    if (!window.confirm(`Delete domain "${d.name}"? This cannot be undone.`)) return;
    setRowSaving((m) => ({ ...m, [d.slug]: true }));
    try {
      await api.delete(`/admin/kb/domains/${d.slug}`);
      toast.success(`Deleted "${d.name}"`);
      await refresh();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setRowSaving((m) => ({ ...m, [d.slug]: false }));
    }
  };

  // ─── unclassified queue ─────────────────────────────────────────────
  const assignUnclassified = async (docId, slug) => {
    if (!slug) return;
    setRowSaving((m) => ({ ...m, [docId]: true }));
    try {
      await api.post(`/admin/kb/documents/${docId}/domain`, { domain: slug });
      const target = domains.find((d) => d.slug === slug);
      toast.success(`Classified into "${target?.name || slug}"`);
      await refresh();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setRowSaving((m) => ({ ...m, [docId]: false }));
    }
  };

  const sortedDomains = useMemo(() => {
    return [...domains].sort((a, b) => {
      if (a.is_system !== b.is_system) return a.is_system ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [domains]);

  // Filter the queue by a substring against source_ref + title.
  const filteredQueue = useMemo(() => {
    const q = queueFilter.trim().toLowerCase();
    if (!q) return unclassified;
    return unclassified.filter((d) => {
      const ref = String(d.source_ref || "").toLowerCase();
      const title = String(d.title || "").toLowerCase();
      return ref.includes(q) || title.includes(q);
    });
  }, [unclassified, queueFilter]);

  const selectedCount = useMemo(
    () => Object.values(selectedIds).filter(Boolean).length,
    [selectedIds],
  );
  const allInViewSelected = filteredQueue.length > 0 &&
    filteredQueue.every((d) => selectedIds[d.id]);

  const toggleSelectAllInView = () => {
    if (allInViewSelected) {
      setSelectedIds((prev) => {
        const next = { ...prev };
        for (const d of filteredQueue) delete next[d.id];
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = { ...prev };
        for (const d of filteredQueue) next[d.id] = true;
        return next;
      });
    }
  };

  const bulkAssign = async () => {
    if (!bulkTarget || selectedCount === 0) return;
    const ids = Object.keys(selectedIds).filter((k) => selectedIds[k]);
    setBulkSaving(true);
    try {
      const { data } = await api.post("/admin/kb/documents/bulk-set-domain", {
        document_ids: ids,
        domain: bulkTarget,
      });
      const targetName = sortedDomains.find((d) => d.slug === bulkTarget)?.name || bulkTarget;
      toast.success(`${data.modified} document(s) classified into "${targetName}"`);
      setSelectedIds({});
      await refresh();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBulkSaving(false);
    }
  };

  return (
    <AppShell>
      <PageHero
        eyebrow="Admin · Shared KB"
        title="KB Domains"
        subtitle="Classify the shared knowledge base — Platform, CPG Industry, Brand Vault, and any custom domains you add. Documents are only reclassified when you say so; nothing is guessed automatically."
      />

      {/* Create domain */}
      <div className="mt-4 flex items-center justify-between">
        <div className="text-sm text-neutral-600">
          <span data-testid="kbdomains-total">{sortedDomains.length}</span> domain(s)
          {" · "}
          <span data-testid="kbdomains-unclassified-count" className="font-semibold text-pink-700">
            {unclassifiedCount}
          </span> unclassified document(s) waiting for review
        </div>
        <Button
          type="button"
          data-testid="kbdomains-new-btn"
          onClick={() => setShowCreate((v) => !v)}
        >
          <Plus className="h-4 w-4 mr-1" />
          {showCreate ? "Cancel" : "New domain"}
        </Button>
      </div>

      {showCreate && (
        <Card className="mt-3 p-4" data-testid="kbdomains-create-form">
          <form onSubmit={submitCreate} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Slug (lowercase, letters/digits/underscore)</Label>
              <Input
                data-testid="kbdomains-slug-input"
                value={draft.slug}
                onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
                placeholder="regulatory_playbooks"
                autoFocus
              />
              {!validSlug && draft.slug.length > 0 && (
                <div className="text-[11px] text-red-600 mt-1">
                  Must start with a letter; 2–40 chars; only lowercase letters, digits, underscore.
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">Name</Label>
              <Input
                data-testid="kbdomains-name-input"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="Regulatory Playbooks"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Description</Label>
              <Textarea
                data-testid="kbdomains-desc-input"
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                rows={2}
                placeholder="What kind of source material belongs in this domain?"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                data-testid="kbdomains-enabled-switch"
                checked={draft.enabled}
                onCheckedChange={(v) => setDraft({ ...draft, enabled: v })}
              />
              <Label className="text-xs">Enabled — surface in Morgan retrieval</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                data-testid="kbdomains-membervis-switch"
                checked={draft.member_visible}
                onCheckedChange={(v) => setDraft({ ...draft, member_visible: v })}
              />
              <Label className="text-xs">Member-visible — surface to regular members (not just admin)</Label>
            </div>
            <div className="md:col-span-2 flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => { setDraft(BLANK_DRAFT); setShowCreate(false); }}>Cancel</Button>
              <Button type="submit" data-testid="kbdomains-create-submit" disabled={!canCreate}>
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                Create
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Registry table */}
      <Card className="mt-4 overflow-hidden" data-testid="kbdomains-table">
        <div className="grid grid-cols-[minmax(140px,1.2fr)_minmax(160px,1.4fr)_minmax(220px,2fr)_100px_130px_150px_50px] text-[11px] uppercase tracking-wider font-semibold text-neutral-500 bg-neutral-50 px-3 py-2">
          <div>Slug</div>
          <div>Name</div>
          <div>Description</div>
          <div>Docs</div>
          <div>Enabled</div>
          <div>Member-visible</div>
          <div></div>
        </div>
        {loading ? (
          <div className="p-6 text-center text-neutral-500 text-sm flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : sortedDomains.length === 0 ? (
          <div className="p-6 text-center text-neutral-500 text-sm">No domains yet.</div>
        ) : (
          sortedDomains.map((d) => {
            const n = counts[d.slug] || 0;
            const busy = !!rowSaving[d.slug];
            return (
              <div key={d.slug} data-testid={`kbdomains-row-${d.slug}`}
                className="grid grid-cols-[minmax(140px,1.2fr)_minmax(160px,1.4fr)_minmax(220px,2fr)_100px_130px_150px_50px] px-3 py-3 border-t border-neutral-100 text-sm items-center">
                <div className="font-mono text-xs text-neutral-700 flex items-center gap-1">
                  {d.is_system ? <Lock className="h-3 w-3 text-neutral-400" /> : <Tag className="h-3 w-3 text-neutral-400" />}
                  {d.slug}
                </div>
                <div className="pr-2">
                  <RowNameEdit
                    value={d.name}
                    disabled={busy}
                    onSave={(v) => patchDomain(d.slug, { name: v })}
                    testid={`kbdomains-name-${d.slug}`}
                  />
                  {d.is_system && (
                    <span data-testid={`kbdomains-system-badge-${d.slug}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-neutral-500 mt-1">
                      <Shield className="h-3 w-3" /> System · undeletable
                    </span>
                  )}
                </div>
                <div className="pr-2">
                  <RowNameEdit
                    value={d.description}
                    disabled={busy}
                    onSave={(v) => patchDomain(d.slug, { description: v })}
                    testid={`kbdomains-desc-${d.slug}`}
                    multiline
                  />
                </div>
                <div className="font-mono text-xs text-neutral-700" data-testid={`kbdomains-count-${d.slug}`}>{n}</div>
                <div>
                  <Switch
                    data-testid={`kbdomains-enabled-${d.slug}`}
                    checked={!!d.enabled}
                    disabled={busy}
                    onCheckedChange={(v) => patchDomain(d.slug, { enabled: v })}
                  />
                </div>
                <div>
                  <Switch
                    data-testid={`kbdomains-membervis-${d.slug}`}
                    checked={!!d.member_visible}
                    disabled={busy}
                    onCheckedChange={(v) => patchDomain(d.slug, { member_visible: v })}
                  />
                </div>
                <div className="text-right">
                  {!d.is_system && (
                    <button
                      type="button"
                      data-testid={`kbdomains-delete-${d.slug}`}
                      disabled={busy}
                      onClick={() => deleteDomain(d)}
                      title={n > 0 ? `Reclassify ${n} doc(s) first` : "Delete domain"}
                      className="text-neutral-400 hover:text-red-600 disabled:opacity-30"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </Card>

      {/* Unclassified queue */}
      <div className="mt-6">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-700 flex items-center gap-2">
          <FolderOpen className="h-4 w-4 text-pink-600" />
          Unclassified — review queue
          <span data-testid="kbdomains-queue-count" className="text-[11px] font-normal text-neutral-500">
            {filteredQueue.length} shown · {unclassifiedCount} total
          </span>
        </h2>
        <p className="text-xs text-neutral-500 mt-1 mb-2">
          Existing docs were never auto-assigned. Filter by source (e.g. <code>module:</code>, <code>worksheet:</code>), tick a batch, then use "Assign selected" — or pick a domain in a single row. Nothing is guessed.
        </p>

        {/* Filter + bulk action bar */}
        <div className="flex items-center gap-2 flex-wrap mb-2" data-testid="kbdomains-bulk-bar">
          <input
            type="text"
            value={queueFilter}
            onChange={(e) => setQueueFilter(e.target.value)}
            placeholder="Filter by source or title (e.g. module:, worksheet:)"
            data-testid="kbdomains-queue-filter"
            className="flex-1 min-w-[220px] text-xs rounded-md border border-neutral-300 px-2 py-1.5 focus:outline-none focus:border-primary bg-white"
          />
          <select
            value={bulkTarget}
            onChange={(e) => setBulkTarget(e.target.value)}
            disabled={selectedCount === 0}
            data-testid="kbdomains-bulk-target"
            className="text-xs rounded-md border border-neutral-300 px-2 py-1.5 focus:outline-none focus:border-primary bg-white disabled:opacity-50"
          >
            <option value="">— Assign selected to… —</option>
            {sortedDomains.filter((d) => d.enabled).map((d) => (
              <option key={d.slug} value={d.slug}>{d.name} ({d.slug})</option>
            ))}
          </select>
          <Button
            type="button"
            onClick={bulkAssign}
            disabled={selectedCount === 0 || !bulkTarget || bulkSaving}
            data-testid="kbdomains-bulk-apply"
            className="text-xs"
          >
            {bulkSaving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
            Assign {selectedCount > 0 ? `${selectedCount} doc(s)` : "selected"}
          </Button>
          {selectedCount > 0 && (
            <button
              type="button"
              onClick={() => setSelectedIds({})}
              data-testid="kbdomains-bulk-clear"
              className="text-xs text-neutral-500 hover:text-neutral-800"
            >clear selection</button>
          )}
        </div>

        <Card className="overflow-hidden" data-testid="kbdomains-queue-table">
          <div className="grid grid-cols-[36px_minmax(180px,1fr)_minmax(200px,2fr)_180px] text-[11px] uppercase tracking-wider font-semibold text-neutral-500 bg-neutral-50 px-3 py-2">
            <div>
              <input
                type="checkbox"
                aria-label="Select all in view"
                data-testid="kbdomains-queue-select-all"
                checked={allInViewSelected}
                onChange={toggleSelectAllInView}
              />
            </div>
            <div>Source</div>
            <div>Title</div>
            <div>Assign to domain</div>
          </div>
          {filteredQueue.length === 0 ? (
            <div className="p-4 text-sm text-center text-emerald-700">
              {queueFilter
                ? "No unclassified docs match this filter."
                : "All shared docs are classified. Nothing to review 🎉"}
            </div>
          ) : (
            filteredQueue.map((doc) => (
              <div key={doc.id} data-testid={`kbdomains-queue-row-${doc.id}`}
                className="grid grid-cols-[36px_minmax(180px,1fr)_minmax(200px,2fr)_180px] items-center px-3 py-2 border-t border-neutral-100 text-sm">
                <div>
                  <input
                    type="checkbox"
                    aria-label={`Select ${doc.title || doc.id}`}
                    data-testid={`kbdomains-queue-check-${doc.id}`}
                    checked={!!selectedIds[doc.id]}
                    onChange={(e) => setSelectedIds((prev) => ({ ...prev, [doc.id]: e.target.checked }))}
                  />
                </div>
                <div className="text-xs text-neutral-500 font-mono truncate">{doc.source_ref}</div>
                <div className="truncate pr-2">{doc.title}</div>
                <div>
                  <select
                    data-testid={`kbdomains-queue-select-${doc.id}`}
                    disabled={!!rowSaving[doc.id]}
                    defaultValue=""
                    onChange={(e) => assignUnclassified(doc.id, e.target.value)}
                    className="text-xs w-full rounded-md border border-neutral-300 px-2 py-1 focus:outline-none focus:border-primary bg-white"
                  >
                    <option value="">— Choose a domain —</option>
                    {sortedDomains.filter((d) => d.enabled).map((d) => (
                      <option key={d.slug} value={d.slug}>
                        {d.name} ({d.slug})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ))
          )}
        </Card>
      </div>
    </AppShell>
  );
}


/**
 * Inline-edit-on-blur text row used for both name + description.
 * Saves only on real change to avoid empty PATCH storms.
 */
function RowNameEdit({ value, onSave, disabled, testid, multiline = false }) {
  const [v, setV]     = useState(value || "");
  useEffect(() => setV(value || ""), [value]);
  const commit = () => {
    const trimmed = v.trim();
    if (trimmed === (value || "").trim()) return;
    onSave(trimmed);
  };
  const Cmp = multiline ? Textarea : Input;
  return (
    <Cmp
      data-testid={testid}
      value={v}
      disabled={disabled}
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (!multiline && e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
      }}
      rows={multiline ? 2 : undefined}
      className="text-sm"
    />
  );
}
